// @ts-nocheck
import React from 'react';
import { Card, CardHeader, Box, Stack, LinearProgress, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import Iconify from '../iconify';

export interface PIISummary {
  piiQueries: number;
  totalQueries?: number;
}

interface PIIWarningCardProps {
  title: string;
  subheader?: string;
  data: PIISummary;
  onPiiClick?: () => void;
}

export default function PIIWarningCard({ title, subheader, data, onPiiClick }: PIIWarningCardProps) {
  const theme = useTheme();
  const piiCount = data?.piiQueries ?? 0;
  const total = data?.totalQueries ?? piiCount;
  const pct = total > 0 ? (piiCount / total) * 100 : 0;

  const item = {
    label: 'PII Queries',
    value: piiCount,
    pct,
    color: theme.palette.error.main,
    icon: 'mdi:shield-alert',
  };

  return (
    <Card>
      <CardHeader title={title} subheader={subheader || `${piiCount} queries contained PII`} />
      <Box sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Box
            onClick={() => onPiiClick && item.value > 0 && onPiiClick()}
            sx={{
              cursor: onPiiClick && item.value > 0 ? 'pointer' : 'default',
              opacity: item.value === 0 ? 0.5 : 1,
              '&:hover': onPiiClick && item.value > 0 ? { bgcolor: alpha(item.color, 0.04), borderRadius: 1 } : {},
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    display: 'flex',
                    borderRadius: 0.75,
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(item.color, 0.12),
                    color: item.color,
                  }}
                >
                  <Iconify icon={item.icon} width={16} />
                </Box>
                <Typography variant="body2">{item.label}</Typography>
              </Stack>
              <Typography variant="subtitle2">
                {item.value}
                {total > 0 && total !== piiCount ? ` (${item.pct.toFixed(1)}%)` : ''}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(item.pct, 100)}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: alpha(item.color, 0.12),
                '& .MuiLinearProgress-bar': { bgcolor: item.color, borderRadius: 1 },
              }}
            />
          </Box>
        </Stack>

        {piiCount > 0 && (
          <Box sx={{ mt: 3, p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.error.main, 0.06) }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Iconify icon="mdi:alert-circle-outline" width={18} sx={{ color: theme.palette.error.main }} />
              <Typography variant="caption" sx={{ color: theme.palette.error.dark }}>
                Personally Identifiable Information detected in queries. Click above to review.
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>
    </Card>
  );
}
